import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './LogicBuildSection.css';

const LogicBuildSection = ({ onEnrollClick }) => {
  const [activeLine, setActiveLine] = useState(0);
  const [dryRunState, setDryRunState] = useState({ n: 5, fact: 1, i: 1 });

  const codeSnippet = [
    { line: 'int n = 5;', state: { n: 5, fact: 1, i: 1 }, comment: 'Initialize n' },
    { line: 'int fact = 1;', state: { n: 5, fact: 1, i: 1 }, comment: 'Initialize factorial' },
    { line: 'for (int i = 1; i <= n; i++) {', state: { n: 5, fact: 1, i: 1 }, comment: 'Loop starts' },
    { line: '    fact = fact * i;', state: { n: 5, fact: 1, i: 1 }, comment: 'fact = 1 * 1' },
    { line: '}', state: { n: 5, fact: 1, i: 2 }, comment: 'Next iteration' },
    { line: '    fact = fact * i;', state: { n: 5, fact: 2, i: 2 }, comment: 'fact = 1 * 2' },
    { line: '    fact = fact * i;', state: { n: 5, fact: 6, i: 3 }, comment: 'fact = 2 * 3' },
    { line: '    fact = fact * i;', state: { n: 5, fact: 24, i: 4 }, comment: 'fact = 6 * 4' },
    { line: '    fact = fact * i;', state: { n: 5, fact: 120, i: 5 }, comment: 'fact = 24 * 5' },
    { line: 'System.out.println(fact);', state: { n: 5, fact: 120, i: 5 }, comment: 'Output: 120' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveLine((prev) => {
        const next = (prev + 1) % codeSnippet.length;
        setDryRunState(codeSnippet[next].state);
        return next;
      });
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="ucp-section logic-build-section">
      <div className="logic-container">
        <div className="logic-content">
          <motion.div 
            className="logic-info"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="logic-badge">EXCLUSIVE ACCESS</span>
            <h2>Master 500+ <br /><span className="blue-text">Logic Building Codes</span></h2>
            <p>
              Get free online access to our premium repository of 500+ interview-essential codes. 
              Visualize every execution with our integrated dry-run methodology.
            </p>
            <div className="logic-stats">
              <div className="l-stat">
                <strong>500+</strong>
                <span>Solved Codes</span>
              </div>
              <div className="l-stat">
                <strong>FREE</strong>
                <span>Online Access</span>
              </div>
              <div className="l-stat">
                <strong>24/7</strong>
                <span>Logic Support</span>
              </div>
            </div>
            <button className="btn-get-access" onClick={onEnrollClick}>
              REGISTER NOW <span>→</span>
            </button>
          </motion.div>

          <motion.div 
            className="logic-visualizer"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="editor-window">
              <div className="editor-header">
                <div className="dots">
                  <span></span><span></span><span></span>
                </div>
                <div className="file-name">Factorial.java</div>
              </div>
              <div className="editor-body">
                <div className="line-numbers">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i}>{i + 1}</div>
                  ))}
                </div>
                <div className="code-content">
                  <pre>
                    <code>
                      <span className="keyword">public class</span> LogicBuilder {'{'}<br />
                      {'  '}<span className="keyword">public static void</span> main(String[] args) {'{'}<br />
                      {codeSnippet.map((item, index) => (
                        <div 
                          key={index} 
                          className={`code-line ${activeLine === index ? 'active' : ''}`}
                        >
                          {item.line}
                          {activeLine === index && (
                            <span className="line-comment">// {item.comment}</span>
                          )}
                        </div>
                      ))}
                      {'  '}{'}'}<br />
                      {'}'}
                    </code>
                  </pre>
                </div>
              </div>
            </div>

            <div className="dry-run-panel">
              <div className="panel-header">DRY RUN VISUALIZER</div>
              <div className="variable-grid">
                <div className="var-item">
                  <span className="var-name">n</span>
                  <motion.span 
                    key={dryRunState.n}
                    initial={{ scale: 1.5, color: '#1675f2' }}
                    animate={{ scale: 1, color: '#fff' }}
                    className="var-value"
                  >
                    {dryRunState.n}
                  </motion.span>
                </div>
                <div className="var-item">
                  <span className="var-name">i</span>
                  <motion.span 
                    key={dryRunState.i}
                    initial={{ scale: 1.5, color: '#1675f2' }}
                    animate={{ scale: 1, color: '#fff' }}
                    className="var-value"
                  >
                    {dryRunState.i}
                  </motion.span>
                </div>
                <div className="var-item">
                  <span className="var-name">fact</span>
                  <motion.span 
                    key={dryRunState.fact}
                    initial={{ scale: 1.5, color: '#10B981' }}
                    animate={{ scale: 1, color: '#fff' }}
                    className="var-value highlight"
                  >
                    {dryRunState.fact}
                  </motion.span>
                </div>
              </div>
              <div className="execution-flow">
                <div className="flow-step">
                  <div className="flow-dot"></div>
                  <span>Line {activeLine + 3}: {codeSnippet[activeLine].comment}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LogicBuildSection;
